import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

interface AiComposerContextValue {
  /** Whether the chat popover is currently visible. */
  isOpen: boolean;
  /** Optional pre-seeded user message text. Reset on close. */
  seededPrompt: string | null;
  /**
   * Optional pre-seeded conversation id. When set, the composer
   * resumes that conversation instead of starting a new one.
   */
  seededConversationId: string | null;
  open: (opts?: { prompt?: string; conversationId?: string }) => void;
  close: () => void;
  toggle: () => void;
  setOpen: (open: boolean) => void;
}

const AiComposerContext = createContext<AiComposerContextValue | null>(null);

/**
 * Wraps the authenticated app shell so any screen can pop the AI
 * composer via `useAiComposer()`. Centralising the open state means
 * the floating omnibar, dashboard suggestions, and any other entry
 * point all share one chat instance — the user can open it from
 * anywhere and never lose conversation context.
 */
export function AiComposerProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [seededPrompt, setSeededPrompt] = useState<string | null>(null);
  const [seededConversationId, setSeededConversationId] = useState<string | null>(null);

  const open = useCallback(
    (opts?: { prompt?: string; conversationId?: string }) => {
      // Always update the seeds — even if already open, the caller
      // wants their prompt to land in the input.
      setSeededPrompt(opts?.prompt ?? null);
      setSeededConversationId(opts?.conversationId ?? null);
      setIsOpen(true);
    },
    [],
  );

  const close = useCallback(() => {
    setIsOpen(false);
    setSeededPrompt(null);
    setSeededConversationId(null);
  }, []);

  const toggle = useCallback(() => {
    setIsOpen((prev) => {
      if (prev) {
        setSeededPrompt(null);
        setSeededConversationId(null);
      }
      return !prev;
    });
  }, []);

  const setOpen = useCallback(
    (next: boolean) => {
      if (next) setIsOpen(true);
      else close();
    },
    [close],
  );

  const value = useMemo<AiComposerContextValue>(
    () => ({
      isOpen,
      seededPrompt,
      seededConversationId,
      open,
      close,
      toggle,
      setOpen,
    }),
    [isOpen, seededPrompt, seededConversationId, open, close, toggle, setOpen],
  );

  return (
    <AiComposerContext.Provider value={value}>
      {children}
    </AiComposerContext.Provider>
  );
}

export function useAiComposer(): AiComposerContextValue {
  const ctx = useContext(AiComposerContext);
  if (!ctx) {
    throw new Error('useAiComposer() must be used inside <AiComposerProvider />.');
  }
  return ctx;
}
